import { API_CONFIG } from '../config/apiConfig';

// --- INTERFACES ---
// This interface is now aligned with your backend's camelCase structure
export interface InsuranceData {
  id: string; // The local UUID from your Node.js backend
  insuranceOwnerType: string;
  insuranceType: string;
  insuranceSubType: string;
  insuranceName: string;
  insuranceStartDate: string;
  insuranceEndDate: string;
  totalInsuranceAmount: string;
  insuranceInstallmentAmount: string;
  insuranceInstallmentFrequency: string;
  totalFamilyInsurance: string;
  totalFamilyInsuranceInstallmentAmount: string;
  status?: string;
  familyMemberId?: string;
  jogetId?: string; // Optional: To store the original Joget PK
}

// Defines the paginated response structure from your Node.js backend
export interface InsurancesApiResponse {
  status: string;
  data: InsuranceData[];
  pagination: {
    total: number;
    size: number;
    currentPage: number;
  };
}

// --- AUTHENTICATION HELPER ---
// Standardized helper to get the JWT Bearer token
const getAuthToken = (): string | null => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    console.error('Authentication Error: JWT Token not found in localStorage.');
    return null;
  }
  return token;
};

// --- API FUNCTIONS ---

/**
 * Fetches all insurance records for the logged-in user from your Node.js backend.
 */
export const fetchInsuranceData = async (page = 1, size = 10): Promise<InsurancesApiResponse> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/insurances?page=${page}&size=${size}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
        const errorBody = await response.json();
        throw new Error(errorBody.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();

  } catch (error) {
    console.error('Error fetching insurance data:', error);
    throw error;
  }
};

/**
 * Creates or updates an insurance record via your Node.js backend.
 */
export const saveOrUpdateInsuranceRecord = async (insurance: InsuranceData): Promise<any> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  // Construct the payload exactly as the backend controller expects
  const payload = {
    id: insurance.id || null, 
    status: insurance.status || 'active',
    request_data: {
      // ✅ CORRECTED: Send camelCase keys to your Node.js backend.
      // Your backend controller will handle the conversion to snake_case for Joget.
      insuranceOwnerType: insurance.insuranceOwnerType,
      insuranceType: insurance.insuranceType,
      insuranceSubType: insurance.insuranceSubType,
      insuranceName: insurance.insuranceName,
      insuranceStartDate: insurance.insuranceStartDate,
      insuranceEndDate: insurance.insuranceEndDate,
      totalInsuranceAmount: insurance.totalInsuranceAmount,
      insuranceInstallmentAmount: insurance.insuranceInstallmentAmount,
      insuranceInstallmentFrequency: insurance.insuranceInstallmentFrequency,
      totalFamilyInsurance: insurance.totalFamilyInsurance,
      totalFamilyInsuranceInstallmentAmount: insurance.totalFamilyInsuranceInstallmentAmount,
      familyMemberId: insurance.familyMemberId,
    }
  };
  
  // This part is correct and should be kept
  if (payload.request_data.familyMemberId === '' || payload.request_data.familyMemberId === undefined) {
      delete payload.request_data.familyMemberId;
  }

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/insurances`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error saving insurance record:`, error);
    throw error;
  }
};

/**
 * Soft deletes an insurance record by its ID via your Node.js backend.
 */
export const deleteInsuranceRecord = async (id: string): Promise<{ success: boolean; message: string }> => {
  const token = getAuthToken();
  if (!token) throw new Error('User not logged in.');

  try {
    const response = await fetch(`${API_CONFIG.API_NODE_BASE_URL}/api/insurances/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return { success: true, message: result.message };
  } catch (error: any) {
    console.error('Error deleting insurance record:', error);
    return { 
      success: false, 
      message: error.message || 'Failed to delete insurance record.' 
    };
  }
};